import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Plus, Users, Edit2, Check, X as XIcon, FileSignature, Download, Trash2, Eye } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { Modal } from "../components/Modal";
import { DataTable } from "../components/DataTable";
import { CustomerContactsModal } from "../components/CustomerContactsModal";
import { SignedFormsModal } from "../components/SignedFormsModal";
import { CustomerDetailModal } from "../components/CustomerDetailModal";
import { initials } from "../lib/format";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Inline editable customer-number chip. Auto-assigned on create; admins can
// override (e.g. to merge two records or follow a paper-trail).
function CustomerNumberCell({ value, customerId, customerName, canEdit, onChanged }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value || "");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setVal(value || ""); }, [value]);

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span className="mono t-strong" data-testid={`cust-num-${customerId}`}
          style={{ background: "var(--card-2)", padding: "2px 8px", borderRadius: 999, fontSize: 12, letterSpacing: ".04em", color: "#6b521a" }}>
          {value || "—"}
        </span>
        {canEdit && (
          <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            title="Override customer number" data-testid={`cust-num-edit-${customerId}`}
            style={{ padding: 2, color: "var(--text-3)" }}>
            <Edit2 size={11} />
          </button>
        )}
      </span>
    );
  }

  const save = async () => {
    const v = val.trim().toUpperCase();
    if (!/^[A-Z]{3}-\d{3,5}$/.test(v)) {
      toast.error("Format must be XXX-NNN (e.g. BAR-001, BAR-1234)");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/customers/${customerId}/number`, { customer_number: v });
      toast.success(`${customerName || "Customer"} renumbered to ${v}`);
      setEditing(false);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        data-testid={`cust-num-input-${customerId}`}
        style={{ width: 80, fontFamily: "var(--mono)", fontSize: 12, padding: "3px 6px", border: "1px solid var(--gold-champ)", borderRadius: 6 }}
      />
      <button className="icon-btn" onClick={save} disabled={busy} title="Save" style={{ color: "#3a7a3a", padding: 2 }} data-testid={`cust-num-save-${customerId}`}><Check size={13} /></button>
      <button className="icon-btn" onClick={() => { setVal(value || ""); setEditing(false); }} title="Cancel" style={{ color: "var(--text-3)", padding: 2 }}><XIcon size={13} /></button>
    </span>
  );
}

// Postal address cell — view + inline edit (pencil-toggle, mirrors CustomerNumberCell).
// Surfaces address_line_1/2, city, province, postal_code, country in one place since
// none of these (besides city) were shown anywhere on the Customers list before.
function AddressCell({ row, canEdit, onChanged }) {
  const [editing, setEditing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [vals, setVals] = React.useState({
    address_line_1: row.address_line_1 || "", address_line_2: row.address_line_2 || "",
    city: row.city || "", province: row.province || "", postal_code: row.postal_code || "",
    country: row.country || "",
  });

  const lines = [row.address_line_1, row.address_line_2,
    [row.city, row.province].filter(Boolean).join(", "),
    [row.postal_code, row.country].filter(Boolean).join(" · ")].filter(Boolean);

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "flex-start", gap: 6 }}>
        <span className="s" style={{ whiteSpace: "pre-line", lineHeight: 1.4 }} data-testid={`cust-addr-${row.id}`}>
          {lines.length ? lines.join("\n") : "—"}
        </span>
        {canEdit && (
          <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            title="Edit address" data-testid={`cust-addr-edit-${row.id}`}
            style={{ padding: 2, color: "var(--text-3)", flexShrink: 0 }}>
            <Edit2 size={11} />
          </button>
        )}
      </span>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${row.id}`, vals);
      toast.success("Address updated");
      setEditing(false);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <span onClick={(e) => e.stopPropagation()}
      style={{ display: "inline-flex", flexDirection: "column", gap: 4, minWidth: 220, padding: "6px 0" }}>
      <input className="input" placeholder="Address line 1" value={vals.address_line_1}
        onChange={(e) => setVals({ ...vals, address_line_1: e.target.value })}
        style={{ padding: "4px 7px", fontSize: 12 }} data-testid={`cust-addr-line1-${row.id}`} />
      <input className="input" placeholder="Address line 2" value={vals.address_line_2}
        onChange={(e) => setVals({ ...vals, address_line_2: e.target.value })}
        style={{ padding: "4px 7px", fontSize: 12 }} data-testid={`cust-addr-line2-${row.id}`} />
      <span style={{ display: "flex", gap: 4 }}>
        <input className="input" placeholder="City" value={vals.city}
          onChange={(e) => setVals({ ...vals, city: e.target.value })} style={{ padding: "4px 7px", fontSize: 12, flex: 1 }} />
        <input className="input" placeholder="Province" value={vals.province}
          onChange={(e) => setVals({ ...vals, province: e.target.value })} style={{ padding: "4px 7px", fontSize: 12, flex: 1 }} />
      </span>
      <span style={{ display: "flex", gap: 4 }}>
        <input className="input" placeholder="Postal code" value={vals.postal_code}
          onChange={(e) => setVals({ ...vals, postal_code: e.target.value })} style={{ padding: "4px 7px", fontSize: 12, flex: 1 }} />
        <input className="input" placeholder="Country" value={vals.country}
          onChange={(e) => setVals({ ...vals, country: e.target.value })} style={{ padding: "4px 7px", fontSize: 12, flex: 1 }} />
      </span>
      <span style={{ display: "flex", gap: 4, marginTop: 2 }}>
        <button className="icon-btn" onClick={save} disabled={busy} title="Save" style={{ color: "#3a7a3a", padding: 2 }} data-testid={`cust-addr-save-${row.id}`}><Check size={13} /></button>
        <button className="icon-btn" onClick={() => setEditing(false)} title="Cancel" style={{ color: "var(--text-3)", padding: 2 }} data-testid={`cust-addr-cancel-${row.id}`}><XIcon size={13} /></button>
      </span>
    </span>
  );
}

// Contacts cell with a portal-rendered hover-peek (escapes table overflow).
function ContactsCell({ value, row, onOpen }) {
  const [hover, setHover] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const ref = React.useRef(null);
  const preview = row.contacts_preview || [];
  const showPop = hover && preview.length > 0;

  const onEnter = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      // Position popover above-or-below + right-aligned to the cell button
      const POP_WIDTH = 300;
      const top = r.bottom + 6;
      const left = Math.max(8, Math.min(window.innerWidth - POP_WIDTH - 8, r.right - POP_WIDTH));
      setPos({ top, left });
    }
    setHover(true);
  };

  return (
    <span ref={ref} className="contacts-cell" onMouseEnter={onEnter} onMouseLeave={() => setHover(false)}
          style={{ position: "relative", display: "inline-block" }}>
      <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }}
              data-testid={`cust-contacts-${row.id}`}>
        <Users size={13} /> {value ?? 0}
      </button>
      {showPop && ReactDOM.createPortal(
        <div className="contacts-pop-portal" data-testid={`cust-contacts-pop-${row.id}`}
             style={{ top: pos.top, left: pos.left }}>
          <span className="contacts-pop-head">
            {value} contact{value === 1 ? "" : "s"} on {row.customer_name}
          </span>
          {preview.map((c, i) => (
            <span key={c.id || i} className="contacts-pop-row">
              <span className="contacts-pop-name">
                {c.is_primary && <span className="contacts-pop-star" title="Primary">★</span>}
                {c.name || "—"}
              </span>
              {c.email && <span className="contacts-pop-email mono">{c.email}</span>}
            </span>
          ))}
          {value > preview.length && (
            <span className="contacts-pop-more">+ {value - preview.length} more — click to manage</span>
          )}
        </div>, document.body)}
    </span>
  );
}



export default function Customers() {
  const { can, user } = useAuth();
  const canEdit = can("customers", "edit");
  const canDelete = ["manager", "admin"].includes(user?.role);
  const canManageContacts = can("users", "edit");
  const canDeleteContacts = ["manager", "admin"].includes(user?.role);
  const canViewForms = can("forms", "view");
  const canAckForms = can("forms", "edit");
  const [refresh, setRefresh] = useState(0);
  const [adding, setAdding] = useState(false);
  const [contactsFor, setContactsFor] = useState(null);
  const [formsFor, setFormsFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({ customer_name: "", contact_person: "", email: "", phone: "", address_line_1: "", address_line_2: "", city: "", province: "", postal_code: "", country: "", pte_online_enabled: false });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/customers/export", { responseType: "blob", timeout: 600000 });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = `customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Export ready — check your downloads");
    } catch (e) { toast.error(apiErr(e)); } finally { setExporting(false); }
  };

  const create = async () => {
    if (!form.customer_name) return toast.error("Customer name is required");
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setAdding(false); setForm({ customer_name: "", contact_person: "", email: "", phone: "", address_line_1: "", address_line_2: "", city: "", province: "", postal_code: "", country: "", pte_online_enabled: false });
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const bulkDelete = async (ids, clear) => {
    if (!window.confirm(
      `Remove ${ids.length} customer${ids.length === 1 ? "" : "s"} from the list?\n\n` +
      `They will be hidden from the customer list but their equipment, jobs, ` +
      `certificates and history are preserved (this is reversible). Continue?`
    )) return;
    try {
      const r = await api.post("/customers/bulk-delete", { ids });
      const linked = (r.data.results || []).reduce((s, x) => s + (x.linked_equipment || 0), 0);
      toast.success(`Removed ${r.data.deleted} customer(s)${linked ? ` · ${linked.toLocaleString()} linked equipment preserved` : ""}`);
      clear && clear();
      setRefresh((x) => x + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "view_details", label: "", render: (_v, row) => (
      <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); setDetailFor(row); }} data-testid={`cust-view-${row.id}`}>
        <Eye size={13} /> View
      </button>
    ) },
    { key: "customer_number", label: "ID", sortable: true, colSearch: true, render: (v, row) => (
      <CustomerNumberCell value={v} customerId={row.id} customerName={row.customer_name} canEdit={canEdit} onChanged={() => setRefresh((x) => x + 1)} />
    ) },
    { key: "customer_name", label: "Customer", sortable: true, editable: true, colSearch: true, render: (v, row) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 11 }}>
        <span className="avatar" style={{ borderRadius: 9 }}>{initials(v)}</span>
        <span className="cell-2"><span className="t-strong">{v}</span><span className="s">{row.city || row.province || "—"}</span></span>
      </span>) },
    { key: "contact_person", label: "Primary contact", sortable: true, editable: true, colSearch: true, render: (v, row) => (
      <span className="cell-2">
        <span className="t-strong">{row.primary_contact_name || v || "—"}</span>
        <span className="s mono">{row.primary_contact_email || row.email || ""}</span>
      </span>
    ) },
    { key: "email", label: "Email", sortable: true, editable: true, colSearch: true, mono: true },
    { key: "phone", label: "Phone", editable: true, mono: true },
    { key: "address_line_1", label: "Address", render: (_v, row) => (
      <AddressCell row={row} canEdit={canEdit} onChanged={() => setRefresh((x) => x + 1)} />
    ) },
    { key: "equipment_count", label: "Equipment", sortable: false, align: "right", render: (v) => <span className="mono t-strong">{(v ?? 0).toLocaleString("en-ZA")}</span> },
    { key: "contacts_count", label: "Contacts", align: "right", render: (v, row) => (
      <ContactsCell value={v} row={row} onOpen={() => setContactsFor(row)} />
    ) },
    ...(canViewForms ? [{
      key: "signed_forms", label: "Forms", align: "right", render: (_v, row) => (
        <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); setFormsFor(row); }} data-testid={`cust-forms-${row.id}`}>
          <FileSignature size={13} /> View
        </button>
      ),
    }] : []),
    { key: "pte_online_enabled", label: "Portal", editable: true, type: "select", options: [{ value: "true", label: "On" }, { value: "false", label: "Off" }], render: (v) => v ? <span className="badge b-gold"><span className="bd" />Portal on</span> : <span className="badge b-slate"><span className="bd" />Off</span> },
  ];

  return (
    <>
      <PageHead crumb="Records" title="Customers"
        sub="Deduplicated canonical companies — sortable, searchable, editable inline. Click the contacts count to manage portal users for each customer."
        actions={<>
          <button className="btn ghost" onClick={exportCsv} disabled={exporting} data-testid="customers-export-btn"><Download size={15} /> {exporting ? "Exporting…" : "Export CSV"}</button>
          {canEdit && <button className="btn gold" onClick={() => setAdding(true)} data-testid="add-customer-btn"><Plus size={15} /> Add customer</button>}
        </>} />

      <DataTable endpoint="/customers" columns={columns} rowKey="id" testid="customers"
        canEdit={canEdit} editEndpoint={(row) => `/customers/${row.id}`}
        selectable={canDelete}
        bulkToolbar={canDelete ? (ids, clear) => (
          <button className="btn danger sm" onClick={() => bulkDelete(ids, clear)} data-testid="customers-bulk-delete">
            <Trash2 size={14} /> Remove selected ({ids.length})
          </button>
        ) : undefined}
        defaultSort={{ by: "customer_name", dir: "asc" }} searchPlaceholder="Search customers…" refreshSignal={refresh} />

      {adding && (
        <Modal open onClose={() => setAdding(false)} title="Add customer" subtitle="Create a new canonical customer record"
          footer={<><button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button><button className="btn gold" onClick={create} data-testid="save-customer-btn">Create customer</button></>}>
          <div className="form-grid">
            <div className="fld"><label>Company name <span className="req">*</span></label><input className="input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="cust-name" /></div>
            <div className="fld"><label>Primary contact</label><input className="input" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div className="fld"><label>Email</label><input className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="fld"><label>Phone</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="fld"><label>Address line 1</label><input className="input" value={form.address_line_1} onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} /></div>
            <div className="fld"><label>Address line 2</label><input className="input" value={form.address_line_2} onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} /></div>
            <div className="fld"><label>City</label><input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="fld"><label>Province</label><input className="input" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
            <div className="fld"><label>Postal code</label><input className="input" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
            <div className="fld"><label>Country</label><input className="input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, fontSize: 13 }}>
            <input type="checkbox" checked={form.pte_online_enabled} onChange={(e) => setForm({ ...form, pte_online_enabled: e.target.checked })} /> Enable customer portal access
          </label>
        </Modal>
      )}

      {contactsFor && (
        <CustomerContactsModal customer={contactsFor} canEdit={canManageContacts} canDelete={canDeleteContacts}
          onClose={() => setContactsFor(null)}
          onChanged={() => setRefresh((x) => x + 1)} />
      )}

      {formsFor && (
        <SignedFormsModal customer={formsFor} canAcknowledge={canAckForms}
          onClose={() => setFormsFor(null)} />
      )}

      {detailFor && (
        <CustomerDetailModal customer={detailFor} canEdit={canEdit}
          onClose={() => setDetailFor(null)}
          onChanged={() => setRefresh((x) => x + 1)} />
      )}
    </>
  );
}
